import type { MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { EtiquetaDePrioridade, EtiquetaDeRisco } from '../../../components/Etiqueta'
import { cn } from '../../../lib/cn'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import type { ResumoGmud } from '../../../types/gmud'
import { SO_A_PARTIR_DE_LARGA, SO_A_PARTIR_DE_MEDIA } from './colunas'
import { EstadoNaLista, SeloDeBloqueio, ValorOuVazio } from './marcadores'

const CELULA = 'px-4 py-3 align-middle text-sm text-slate-700'

/**
 * Uma solicitação na tabela.
 *
 * A linha inteira abre o detalhe, mas quem faz isso é o `onClick` do `<tr>` — sem `role` e sem
 * `tabIndex`. O caminho do teclado continua sendo o link do código, e só ele: dar foco também à
 * linha faria quem navega por teclado passar duas vezes pela mesma solicitação em cada uma das
 * vinte linhas da página, e transformar a `<tr>` em `role="link"` custaria a semântica de linha
 * de tabela que o leitor de tela usa para anunciar "Estado: aguardando pauta".
 *
 * O registro importado do sistema anterior ganha um tratamento próprio e sóbrio — fundo ardósia
 * e um filete na borda esquerda — porque o que ele não pode é passar por registro do processo
 * novo. Cinza fechado, e não uma cor de aviso: ele não é problema nem pendência, é história.
 *
 * @param props a linha da listagem
 * @returns a linha da tabela
 */
export function LinhaDeGmud({ gmud }: { gmud: ResumoGmud }) {
  const navegar = useNavigate()
  const migrado = gmud.origem === 'MIGRADO_V1'

  function aoClicar(evento: MouseEvent<HTMLTableRowElement>) {
    // O link do código já navega sozinho. Sem esta guarda, o clique nele sobe até aqui e
    // empilha uma segunda entrada no histórico — e o "voltar" do navegador passa a exigir
    // dois cliques para sair de uma solicitação que se abriu com um.
    if (evento.target instanceof Element && evento.target.closest('a')) {
      return
    }
    // Selecionar o código com o mouse para colar num e-mail é o que o fiscal faz o dia
    // inteiro, e não é um pedido para abrir a solicitação.
    if (window.getSelection()?.toString()) {
      return
    }
    navegar(`/gmuds/${gmud.id}`)
  }

  return (
    <tr
      onClick={aoClicar}
      className={cn(
        'cursor-pointer border-b border-slate-100 transition-colors',
        migrado ? 'bg-slate-100 hover:bg-slate-200/70' : 'odd:bg-white even:bg-slate-50/60 hover:bg-niteroi-50',
      )}
    >
      <td className={cn(CELULA, 'border-l-4', migrado ? 'border-l-slate-400' : 'border-l-transparent')}>
        <div className="flex items-center gap-2">
          <Link
            to={`/gmuds/${gmud.id}`}
            className="font-semibold text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
          >
            {gmud.codigo}
          </Link>
          {gmud.bloqueadoParaDeploy && <SeloDeBloqueio />}
        </div>
      </td>
      <td className={cn(CELULA, 'tabular-nums')}>
        <ValorOuVazio texto={gmud.chamadoNumero} />
      </td>
      <td className={cn(CELULA, SO_A_PARTIR_DE_LARGA)}>
        <ValorOuVazio texto={gmud.sistema} />
      </td>
      <td className={CELULA}>
        <EstadoNaLista gmud={gmud} />
      </td>
      <td className={CELULA}>
        <EtiquetaDePrioridade prioridade={gmud.prioridade} rotulo={gmud.prioridadeRotulo} />
      </td>
      <td className={cn(CELULA, SO_A_PARTIR_DE_MEDIA)}>
        <EtiquetaDeRisco risco={gmud.riscoNivel} />
      </td>
      <td className={cn(CELULA, SO_A_PARTIR_DE_LARGA)}>
        <ValorOuVazio texto={gmud.solicitante} />
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap tabular-nums')}>
        <time dateTime={gmud.criadoEm} title={dataHoraPorExtenso(gmud.criadoEm)}>
          {data(gmud.criadoEm)}
        </time>
      </td>
    </tr>
  )
}
